import { type QueryClient, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AddYoutubeVideoInput,
  CreateYoutubePlaylistInput,
  UpdateYoutubePlaylistInput,
  UpdateYoutubeVideoInput,
  YoutubePlaylistListItem,
  YoutubeQuotaSnapshot,
  YoutubeVideoRecord,
} from '@news-cms/shared';
import { ApiError, api } from './api';

/** Mirror of the backend `isQuotaError` heuristic — covers the same
 *  signatures (raw Google body, JSON-wrapped reason, our own
 *  "Quota guard:" string from the cron skip path). */
export function isYouTubeQuotaError(text: string | null | undefined): boolean {
  if (!text) return false;
  const lower = text.toLowerCase();
  return (
    lower.includes('quotaexceeded') ||
    lower.includes('dailylimitexceeded') ||
    lower.includes('exceeded your quota') ||
    lower.includes('quota guard:') ||
    lower.includes('exceeded your \\u003ca href')
  );
}

/* ---------- Queries ---------- */

export const ytPlaylistsQueryOptions = queryOptions({
  queryKey: ['youtube', 'playlists'] as const,
  queryFn: () =>
    api<{ items: YoutubePlaylistListItem[] }>('/api/admin/youtube-playlists').then((d) => d.items),
  staleTime: 30_000,
});

/** Read-only quota snapshot. 60s staleTime + 5-min refetch so the
 *  banner reflects in-flight consumption without hammering KV. */
export const ytQuotaQueryOptions = queryOptions({
  queryKey: ['youtube', 'quota'] as const,
  queryFn: () => api<YoutubeQuotaSnapshot>('/api/admin/youtube-playlists/_quota'),
  staleTime: 60_000,
  refetchInterval: 5 * 60_000,
});

export const ytPlaylistDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['youtube', 'playlist', id] as const,
    queryFn: () =>
      api<{ playlist: YoutubePlaylistListItem }>(`/api/admin/youtube-playlists/${id}`).then(
        (d) => d.playlist,
      ),
    staleTime: 30_000,
  });

export interface VideosPage {
  items: YoutubeVideoRecord[];
  total: number;
  limit: number;
  offset: number;
}

export const ytPlaylistVideosQueryOptions = (
  id: string,
  params: { limit: number; offset: number },
) =>
  queryOptions({
    queryKey: ['youtube', 'playlist', id, 'videos', params] as const,
    queryFn: () =>
      api<VideosPage>(
        `/api/admin/youtube-playlists/${id}/videos?limit=${params.limit}&offset=${params.offset}`,
      ),
    staleTime: 15_000,
  });

/* ---------- Mutations ---------- */

function invalidateAll(qc: QueryClient, playlistId?: string): void {
  void qc.invalidateQueries({ queryKey: ['youtube', 'playlists'] });
  if (playlistId) {
    void qc.invalidateQueries({ queryKey: ['youtube', 'playlist', playlistId] });
  }
}

export function useCreateYtPlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateYoutubePlaylistInput) =>
      api<{ playlist: YoutubePlaylistListItem }>('/api/admin/youtube-playlists', {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((d) => d.playlist),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateYtPlaylist(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateYoutubePlaylistInput) =>
      api<{ playlist: YoutubePlaylistListItem }>(`/api/admin/youtube-playlists/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }).then((d) => d.playlist),
    onSuccess: () => {
      invalidateAll(qc, id);
      // Videos may have been cleared (if upstream playlist_id changed) —
      // refetch the per-playlist video list too.
      void qc.invalidateQueries({ queryKey: ['youtube', 'playlist', id, 'videos'] });
    },
  });
}

export function useDeleteYtPlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/admin/youtube-playlists/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function usePollYtPlaylist(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: true; considered: number; inserted: number }>(
        `/api/admin/youtube-playlists/${id}/poll`,
        { method: 'POST' },
      ),
    onSuccess: () => invalidateAll(qc, id),
  });
}

export function useAddYtVideo(playlistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddYoutubeVideoInput) =>
      api<{ video: YoutubeVideoRecord }>(`/api/admin/youtube-playlists/${playlistId}/videos`, {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((d) => d.video),
    onSuccess: () => invalidateAll(qc, playlistId),
  });
}

export function useUpdateYtVideo(playlistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ videoId, input }: { videoId: string; input: UpdateYoutubeVideoInput }) =>
      api<{ video: YoutubeVideoRecord }>(`/api/admin/youtube-playlists/videos/${videoId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }).then((d) => d.video),
    onSuccess: () => invalidateAll(qc, playlistId),
  });
}

export function useDeleteYtVideo(playlistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (videoId: string) =>
      api<{ ok: true }>(`/api/admin/youtube-playlists/videos/${videoId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAll(qc, playlistId),
  });
}

/** Batch-fetch duration + channel metadata for all videos in the playlist
 * via YT Data API videos.list. Costs ceil(N/50) quota units. */
export function useRefreshYtMetadata(playlistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: true; refreshed: number; missing: number }>(
        `/api/admin/youtube-playlists/${playlistId}/refresh-metadata`,
        { method: 'POST' },
      ),
    onSuccess: () => invalidateAll(qc, playlistId),
  });
}

export type BulkVideoAction = 'hide' | 'show' | 'delete';

/** Bulk hide/show/delete videos in a playlist. Returns affected count. */
export function useBulkVideoAction(playlistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, action }: { ids: string[]; action: BulkVideoAction }) =>
      api<{ ok: true; affected: number }>(
        `/api/admin/youtube-playlists/${playlistId}/videos/bulk`,
        { method: 'POST', body: JSON.stringify({ ids, action }) },
      ),
    onSuccess: () => invalidateAll(qc, playlistId),
  });
}

/** Atomically set sort_order on playlists based on the ordered id list.
 * `orderedIds[0]` → sort_order 0, [1] → 1, etc. Used by drag-and-drop
 * on the /videos page. */
export function useReorderYtPlaylists() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      api<{ ok: true; updated: number }>('/api/admin/youtube-playlists/reorder', {
        method: 'POST',
        body: JSON.stringify({ orderedIds }),
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Atomically set positions on all videos based on the ordered id list.
 * `orderedIds[0]` → position 0, [1] → 1, etc. */
export function useReorderYtVideos(playlistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      api<{ ok: true; updated: number }>(
        `/api/admin/youtube-playlists/${playlistId}/videos/reorder`,
        { method: 'POST', body: JSON.stringify({ orderedIds }) },
      ),
    onSuccess: () => invalidateAll(qc, playlistId),
  });
}

/* ---------- Error mapping ---------- */

export function ytErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  switch (e.code) {
    case 'INVALID_PLAYLIST_ID':
      return 'ใส่ลิงก์ playlist หรือ playlist ID ที่ถูกต้อง';
    case 'INVALID_VIDEO_ID':
      return 'ใส่ลิงก์ video หรือ video ID ที่ถูกต้อง';
    case 'SLUG_TAKEN':
      return 'slug นี้ใช้แล้ว เปลี่ยนเป็นชื่ออื่น';
    case 'VIDEO_EXISTS':
      return 'วิดีโอนี้อยู่ใน playlist นี้แล้ว';
    case 'PLAYLIST_INACTIVE':
      return 'Playlist ปิดอยู่ — เปิดใช้งานก่อนแล้วลองใหม่';
    case 'SERVICE_UNAVAILABLE':
      return 'YouTube API ยังไม่ได้ตั้งค่า — ติดต่อ admin';
    case 'UPSTREAM_NOT_FOUND':
      return 'YouTube หาไม่เจอ (อาจถูกลบ, ตั้งเป็น private หรือ ID ผิด)';
    case 'UPSTREAM_ERROR':
      return 'YouTube ตอบกลับ error — ลองใหม่ภายหลัง';
    case 'VALIDATION_ERROR':
      return 'ข้อมูลไม่ถูกต้อง — ตรวจ URL / ค่าอื่นแล้วลองใหม่';
    case 'NOT_FOUND':
      return 'ไม่พบรายการ — refresh แล้วลองใหม่';
    case 'FORBIDDEN':
      return 'ไม่มีสิทธิ์ — ต้องมี feed.manage';
    case 'VIDEO_IN_USE':
    case 'PLAYLIST_HAS_REFERENCED_VIDEOS':
      // Server's Thai message is already actionable; pass it through so
      // the toast surfaces which group(s) are blocking.
      return e.message;
    case 'SYSTEM_PLAYLIST':
      return e.message;
    default:
      return e.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  }
}
